import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// The PNR-group operation center (the `RosterPnrDrawer`/`components/pnr/*`
// family) renders against ONE scope-neutral row shape plus a scope tag, so the
// same family serves both an entire disruption (control center) and a single
// case (case-detail). The row shape is IDENTICAL at both scopes — the BFF serves
// the same `DisruptionPnrGroupRow` from `GET /v1/disruptions/:urn/passengers`
// and `GET /v1/cases/:caseUrn/pnr-groups` (the case-scoped projection added for
// the cases↔disruptions UI convergence). `DisruptionPnrGroupRow` is kept as the
// canonical name; `PnrGroupRow` is the scope-neutral alias new code should use.
export type PnrGroupRow = DisruptionPnrGroupRow;

/**
 * Which axis the operation center is scoped to.
 *
 * Note `caseUrn` is the per-row WebSocket room-join + booking-body key at BOTH
 * scopes — a disruption spans N cases, so each row still joins its own case room
 * for live reservation/booking events (the proven `roster-pnr-drawer` pattern).
 * The scope here only governs the bulk/list reads + mutation cache invalidation.
 */
export type PnrGroupScope =
  | { kind: 'disruption'; disruptionUrn: string }
  | { kind: 'case'; caseUrn: string };

/** The cache-invalidation key prefix for a scope — disruption roster vs. case
 *  PNR-group roster. Mutations under a scope invalidate this list slice. */
export function pnrGroupScopeKey(scope: PnrGroupScope): readonly unknown[] {
  return scope.kind === 'disruption'
    ? ['disruptions', scope.disruptionUrn, 'roster']
    : ['cases', scope.caseUrn, 'pnr-groups'];
}

/**
 * The disruption URN that ROUTES a disruption-scoped care action (one-click
 * allocate / issue voucher / exclude) for a given row. At disruption scope it's
 * the scope's URN; at case scope it's the row's parent `disruptionUrn` (the
 * action operates on the sub-case — the disruption is just the routing prefix).
 * `undefined` for a standalone case (no parent disruption) — callers hide the
 * action in that case.
 */
export function routingDisruptionUrn(
  scope: PnrGroupScope,
  row: { disruptionUrn?: string },
): string | undefined {
  return scope.kind === 'disruption' ? scope.disruptionUrn : row.disruptionUrn;
}
